import React from "react";
import { PageHeader, PageLoader, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { useMilestones } from "@/components/CelebrationMoment";
import { ReviewsReferrals } from "@/components/ReviewsReferrals";
import { CalendarHeart, TrendingUp, KeyRound } from "lucide-react";

function anniversaryInfo(closing) {
  const cd = new Date(closing + "T12:00:00");
  const now = new Date();
  let years = now.getFullYear() - cd.getFullYear();
  const thisYear = new Date(now.getFullYear(), cd.getMonth(), cd.getDate(), 12);
  if (thisYear > now) years -= 1;
  const next = thisYear > now ? thisYear : new Date(now.getFullYear() + 1, cd.getMonth(), cd.getDate(), 12);
  const days = Math.ceil((next - now) / 86400000);
  return { years: Math.max(0, years), days, next, cd };
}

function ValueUpdate({ u }) {
  const range = u.value_low && u.value_high ? `${money(u.value_low)} – ${money(u.value_high)}` : null;
  return (
    <div className="p-4 rounded-xl bg-secondary" data-testid={`anniversary-update-${u.id}`}>
      <p className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
      {u.estimated_value ? <p className="font-heading text-2xl font-bold mt-1">{money(u.estimated_value)}</p> : null}
      {range && <p className="text-sm text-muted-foreground">Estimated range: {range}</p>}
      {u.note && <p className="text-sm mt-2 whitespace-pre-line">{u.note}</p>}
    </div>
  );
}

export default function Anniversary() {
  const { data, isLoading } = useMilestones();
  if (isLoading) return <PageLoader />;
  const updates = data.anniversary_updates || [];
  const info = data.closing_date ? anniversaryInfo(data.closing_date) : null;
  return (
    <div>
      <PageHeader title="Home anniversary" subtitle="A yearly check-in on your home, with an estimated value update from Marlene." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 rounded-2xl bg-forest text-white border-0" data-testid="anniversary-summary">
            <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-wider"><CalendarHeart className="h-4 w-4" /> Your home</div>
            {info ? (
              <>
                <p className="font-heading text-2xl font-semibold mt-3" data-testid="anniversary-years">
                  {info.years === 0 ? "Your first year in your home" : `${info.years} year${info.years > 1 ? "s" : ""} in your home`}
                </p>
                <p className="text-white/80 text-sm mt-1">Keys since {info.cd.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} · next anniversary in {info.days} day{info.days === 1 ? "" : "s"}</p>
              </>
            ) : <p className="font-heading text-xl mt-3">Your closing date will appear here once Marlene adds it.</p>}
          </Card>
          <Card className="p-6 rounded-2xl">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-primary" /> Estimated value updates</h2>
            {updates.length ? <div className="space-y-3">{updates.map((u) => <ValueUpdate key={u.id} u={u} />)}</div>
              : <p className="text-sm text-muted-foreground" data-testid="anniversary-empty"><KeyRound className="h-4 w-4 inline mr-1" />Marlene will add your first estimated value update around your home anniversary.</p>}
            <p className="text-[11px] text-muted-foreground mt-4">Estimate only, not an appraisal. Based on general market information; actual value can only be set by a licensed appraiser or a sale.</p>
          </Card>
        </div>
        <ReviewsReferrals reviewUrl={data.review_url} />
      </div>
    </div>
  );
}
